import {MinusIcon, PlusIcon} from '@heroicons/react/solid';
import {useState} from 'react';
import {useNavigate} from 'react-router-dom';

import {Badge, Button, Table, TBody, Td, Th, Thead, Tr} from '@/components';
import type {BusinessRFI} from '@/types';
import {BusinessRFISource, BusinessRFIStatus} from '@/types';
import * as formatDate from '@/utils/date';
import {formatProvider} from '@/utils/merchant';

import BusinessRFISummary from './BusinessRFISummary';
import {getOutstandingDocuments, rfiStatusColorScheme} from './rfiStatus';

const BusinessRFITable = ({
  items,
  businessName,
  dataCy,
}: {
  items: BusinessRFI[];
  businessName: string;
  dataCy?: string;
}) => {
  return (
    <Table
      containerDataCy={dataCy ? `${dataCy}-table-container` : undefined}
      className="w-max"
    >
      <Thead>
        <Tr>
          <Th className="w-20">&nbsp;</Th>
          <Th className="px-4">Requested Documents</Th>
          <Th className="px-4">For</Th>
          <Th className="px-4">Raised By</Th>
          <Th className="px-4">Status</Th>
          <Th className="px-4">Outstanding</Th>
          <Th className="px-4">Updated</Th>
          <Th className="px-4">Action</Th>
        </Tr>
      </Thead>
      <TBody>
        {items.map((item, index) => (
          <BusinessRFIRow
            dataCy={dataCy}
            key={item.id ?? index}
            item={item}
            index={index}
            businessName={businessName}
          />
        ))}
      </TBody>
    </Table>
  );
};

const BusinessRFIRow = ({
  item,
  index,
  businessName,
  dataCy,
}: {
  item: BusinessRFI;
  index: number;
  businessName: string;
  dataCy?: string;
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const navigate = useNavigate();
  const zebraRow = index % 2 === 0 ? '' : 'bg-gray-50 dark:bg-dark-zebraRow';
  const requested = item.requestedDocuments ?? [];
  const outstanding = getOutstandingDocuments(item);

  return (
    <>
      <Tr className={zebraRow}>
        <Td isNumeric>
          <Button
            dataCy={dataCy ? `${dataCy}-expand` : undefined}
            onClick={() => setIsExpanded(!isExpanded)}
            variant="unStyled"
          >
            <div
              className={`border-2 border-gray-700 dark:border-dark-borderLight dark:text-gray-100 text-gray-700 w-5 h-5 flex justify-center rounded hover:bg-cyan-50 ${
                isExpanded ? 'bg-cyan-50' : ''
              }`}
            >
              {isExpanded ? (
                <MinusIcon className="w-4 h-4" />
              ) : (
                <PlusIcon className="w-4 h-4" />
              )}
            </div>
          </Button>
        </Td>
        <Td
          data-cy={dataCy ? `${dataCy}-requested-documents` : undefined}
          className="px-4"
        >
          {requested.length ? requested.join(', ') : '-'}
        </Td>
        {/* A processor-raised request reads very differently from one a human opened, and the held payment is the first thing an operator wants. */}
        <Td data-cy={dataCy ? `${dataCy}-for` : undefined} className="px-4">
          {item.customerEmail || businessName || '-'}
        </Td>

        <Td
          data-cy={dataCy ? `${dataCy}-raised-by` : undefined}
          className="px-4"
        >
          {item.source === BusinessRFISource.Processor ? (
            <div className="flex flex-col whitespace-nowrap">
              <span>
                {item.processor ? formatProvider(item.processor) : 'Processor'}
              </span>
            </div>
          ) : (
            'Admin'
          )}
        </Td>
        <Td className="px-4">
          <Badge
            dataCy={dataCy ? `${dataCy}-status-badge` : undefined}
            label={item.status}
            colorScheme={rfiStatusColorScheme(item.status)}
          />
        </Td>
        <Td
          data-cy={dataCy ? `${dataCy}-outstanding` : undefined}
          className="px-4"
        >
          {/* A draft has no ask yet, so "0/0" would read exactly like a fully
              satisfied request. Say there is nothing to count instead. */}
          {item.status === BusinessRFIStatus.Draft ? (
            <span className="text-gray-500 dark:text-gray-400">-</span>
          ) : (
            `${outstanding.length}/${requested.length}`
          )}
        </Td>
        <Td data-cy={dataCy ? `${dataCy}-updated` : undefined} className="px-4">
          {item.updatedAt ? formatDate.DDMonYYYY(String(item.updatedAt)) : '-'}
        </Td>
        <Td className="px-4">
          <Button
            dataCy={dataCy ? `${dataCy}-view-logs` : undefined}
            variant="outline"
            colorScheme="cyan"
            className="!py-1 !px-2"
            onClick={() =>
              navigate(
                `/merchants/businesses/${item.businessId}/rfi/${item.id}/logs`,
              )
            }
          >
            <span className="text-xs">View Logs</span>
          </Button>
        </Td>
      </Tr>

      {isExpanded && (
        <Tr enableHover={false}>
          <Td colspan={8} className="p-0">
            <div className="w-0 min-w-full whitespace-normal">
              <div className="flex py-10 px-8 justify-center bg-sky-50 dark:bg-transparent">
                <BusinessRFISummary
                  dataCy={dataCy}
                  item={item}
                  businessName={businessName}
                />
              </div>
            </div>
          </Td>
        </Tr>
      )}
    </>
  );
};

export default BusinessRFITable;
